import axios, { type AxiosRequestConfig, type AxiosError, type AxiosResponse } from "axios";

import { t } from "@/locales/i18n";
import userStore from "@/store/userStore";

import { toast } from "sonner";
import type { Result } from "#/api";
import { ResultEnum } from "#/enum";

const isDev = import.meta.env.DEV;

const baseURL = isDev ? "/service-api" : import.meta.env.VITE_APP_BASE_API;

const axiosInstance = axios.create({
	baseURL,
	timeout: 100000,
	headers: { "Content-Type": "application/json;charset=utf-8" },
});

axiosInstance.interceptors.request.use(
	(config) => {
		const { userToken } = userStore.getState();
		if (userToken.token) {
			config.headers.Authorization = `Bearer ${userToken.token}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

axiosInstance.interceptors.response.use(
	(res: AxiosResponse<any>) => {

		// Check if response has code field (new API format)
		if (res.data && typeof res.data.code === 'number') {
			if (res.data.code === 0) {
				return res.data.data;
			} else {
				throw new Error(res.data.message || t("sys.api.apiRequestFailed"));
			}
		}

		// Return original response for APIs without code field (old format)
		return res.data;
	},
	(error: AxiosError<Result>) => {
		const { response, message } = error || {};
		const errMsg = response?.data?.error || message || t("sys.api.errorMessage");

		const status = response?.status;
		if (status === 401) {
			userStore.getState().actions.clearUserInfoAndToken();
		} else if (status === 403) {
			toast.error(errMsg, {
				position: "top-center",
				closeButton: true,
				duration: Number.POSITIVE_INFINITY,
			});
		} else {
			toast.error(errMsg, {
				position: "top-center",
			});
		}

		return Promise.reject(error);
	},
);

class APIClient {
	get<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "GET" });
	}

	post<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "POST" });
	}

	put<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "PUT" });
	}
	patch<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "PATCH" });
	}

	delete<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "DELETE" });
	}

	request<T = any>(config: AxiosRequestConfig): Promise<T> {
		return new Promise((resolve, reject) => {
			axiosInstance
				.request<any, AxiosResponse<Result>>(config)
				.then((res: AxiosResponse<Result>) => {
					resolve(res as unknown as Promise<T>);
				})
				.catch((e: Error | AxiosError) => {
					reject(e);
				});
		});
	}
}
export default new APIClient();
